/**
 * Analytics utility for GA4 and Meta Pixel tracking
 * Only loads scripts if environment variables are set
 */

// GA4 Measurement ID from environment
const GA4_MEASUREMENT_ID = process.env.REACT_APP_GA4_MEASUREMENT_ID;

// Meta Pixel ID from environment
const META_PIXEL_ID = process.env.REACT_APP_META_PIXEL_ID;

// Track if scripts have been initialized
let ga4Initialized = false;
let metaPixelInitialized = false;

/**
 * Initialize GA4 (Google Analytics 4)
 */
export function initGA4() {
  if (!GA4_MEASUREMENT_ID || ga4Initialized) {
    return;
  }

  // Load gtag.js script
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_MEASUREMENT_ID}`;
  document.head.appendChild(script);

  // Initialize gtag
  window.dataLayer = window.dataLayer || [];
  function gtag() {
    window.dataLayer.push(arguments);
  }
  window.gtag = gtag;

  gtag('js', new Date());
  gtag('config', GA4_MEASUREMENT_ID, {
    page_path: window.location.pathname,
  });

  ga4Initialized = true;
  console.log('GA4 initialized:', GA4_MEASUREMENT_ID);
}

/**
 * Initialize Meta (Facebook) Pixel
 */
export function initMetaPixel() {
  if (!META_PIXEL_ID || metaPixelInitialized) {
    return;
  }

  // Meta Pixel base code
  !function(f,b,e,v,n,t,s)
  {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
  n.callMethod.apply(n,arguments):n.queue.push(arguments)};
  if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
  n.queue=[];t=b.createElement(e);t.async=!0;
  t.src=v;s=b.getElementsByTagName(e)[0];
  s.parentNode.insertBefore(t,s)}(window, document,'script',
  'https://connect.facebook.net/en_US/fbevents.js');

  window.fbq('init', META_PIXEL_ID);
  window.fbq('track', 'PageView');

  metaPixelInitialized = true;
  console.log('Meta Pixel initialized:', META_PIXEL_ID);
}

/**
 * Initialize all analytics
 */
export function initAnalytics() {
  initGA4();
  initMetaPixel();
}

/**
 * Track page view
 */
export function trackPageView(path, title) {
  // GA4
  if (GA4_MEASUREMENT_ID && window.gtag) {
    window.gtag('event', 'page_view', {
      page_path: path,
      page_title: title,
    });
  }

  // Meta Pixel
  if (META_PIXEL_ID && window.fbq) {
    window.fbq('track', 'PageView');
  }
}

/**
 * Track custom event
 */
export function trackEvent(eventName, params = {}) {
  // GA4
  if (GA4_MEASUREMENT_ID && window.gtag) {
    window.gtag('event', eventName, params);
  }

  // Meta Pixel - map common events
  if (META_PIXEL_ID && window.fbq) {
    const metaEventMap = {
      // Form submissions
      'form_submit': 'Lead',
      'group_enquiry_submit': 'Lead',
      'event_listing_submit': 'Lead',
      
      // Calendar interactions
      'calendar_subscribe_clicked': 'Subscribe',
      'calendar_ics_download': 'Subscribe',
      
      // Badge/Embed interactions
      'badge_embed_copied': 'ViewContent',
      'calendar_widget_embed_copied': 'ViewContent',
      
      // Content views
      'blog_post_viewed': 'ViewContent',
      'event_detail_viewed': 'ViewContent',
    };

    const metaEvent = metaEventMap[eventName];
    if (metaEvent) {
      window.fbq('track', metaEvent, params);
    } else {
      window.fbq('trackCustom', eventName, params);
    }
  }

  // Log to console in development
  if (process.env.NODE_ENV === 'development') {
    console.log('Analytics Event:', eventName, params);
  }
}

/**
 * Track form submission (conversion event)
 */
export function trackFormSubmission(formType, formData = {}) {
  const eventParams = {
    form_type: formType,
    ...formData,
  };

  // GA4
  if (GA4_MEASUREMENT_ID && window.gtag) {
    window.gtag('event', 'generate_lead', eventParams);
  }

  // Meta Pixel
  if (META_PIXEL_ID && window.fbq) {
    window.fbq('track', 'Lead', {
      content_name: formType,
      ...formData,
    });
  }

  console.log('Form Submission Tracked:', formType, formData);
}

/**
 * Track calendar subscribe click
 */
export function trackCalendarSubscribe(filterType, filterValue, eventCount) {
  const params = {
    filter_type: filterType,
    filter_value: filterValue,
    event_count: eventCount,
  };

  // GA4
  if (GA4_MEASUREMENT_ID && window.gtag) {
    window.gtag('event', 'calendar_subscribe', params);
  }

  // Meta Pixel
  if (META_PIXEL_ID && window.fbq) {
    window.fbq('track', 'Subscribe', params);
  }

  console.log('Calendar Subscribe Tracked:', params);
}

/**
 * Track badge/embed engagement
 */
export function trackEmbedEngagement(embedType, eventId, eventName) {
  const params = {
    embed_type: embedType,
    event_id: eventId,
    event_name: eventName,
  };

  // GA4
  if (GA4_MEASUREMENT_ID && window.gtag) {
    window.gtag('event', 'embed_engagement', params);
  }

  // Meta Pixel
  if (META_PIXEL_ID && window.fbq) {
    window.fbq('trackCustom', 'EmbedEngagement', params);
  }

  console.log('Embed Engagement Tracked:', params);
}

/**
 * Track blog/content view
 */
export function trackContentView(contentType, contentId, contentTitle) {
  const params = {
    content_type: contentType,
    content_id: contentId,
    content_title: contentTitle,
  };

  // GA4
  if (GA4_MEASUREMENT_ID && window.gtag) {
    window.gtag('event', 'view_content', params);
  }

  // Meta Pixel
  if (META_PIXEL_ID && window.fbq) {
    window.fbq('track', 'ViewContent', {
      content_type: contentType,
      content_ids: [contentId],
      content_name: contentTitle,
    });
  }

  console.log('Content View Tracked:', params);
}

export default {
  initAnalytics,
  initGA4,
  initMetaPixel,
  trackPageView,
  trackEvent,
  trackFormSubmission,
  trackCalendarSubscribe,
  trackEmbedEngagement,
  trackContentView,
};
